/**
 * Page Designer Grid V1.
 *
 * Screen = Canvas, Module = Card, Grid = Structure.
 * The grid is the source of truth for layout:
 *   column, row, width, height in integer cells
 * Cells are always square. Width drives cell size; height is derived.
 * This is application screen layout — not printable report layout.
 * No EMR/FHIR data is attached to cards yet.
 * RTF Text is static formatted HTML stored on the card (rtfHtml).
 */

import { sanitizeRtfHtml } from "@/lib/pageDesignerRtf";
import {
  CONTENT_TYPE_TABLE,
  TABLE_PREFERRED_HEIGHT,
  TABLE_PREFERRED_WIDTH,
  emptyTableContent,
  sanitizeTableContent,
  tableContentEquals,
} from "@/lib/pageDesignerTable";

export const GRID_COLUMNS = 30;
export const GRID_ROWS = 20;
/** Base design unit in CSS pixels. Actual cell size scales with container width. */
export const CELL_SIZE = 48;
export const MIN_CARD_SIZE = 1;
/** Floor for exact-pixel Width/Height entry (Snap to Grid off), in CSS pixels. */
export const MIN_PIXEL_CARD_SIZE = 20;

export function gridSizeKey(columns = GRID_COLUMNS, rows = GRID_ROWS) {
  return `${columns}x${rows}`;
}

export function gridSizeLabel(columns = GRID_COLUMNS, rows = GRID_ROWS) {
  return `${columns} × ${rows} Grid`;
}

export const GRID_SIZE_LABEL = gridSizeLabel();

export const CONTENT_TYPE_BLANK = "blank";
export const CONTENT_TYPE_RTF = "rtf";
export { CONTENT_TYPE_TABLE };
export const BEHAVIOR_NONE = "none";

/** Content Type options. Blank, RTF Text, and Table are functional in this phase. */
export const CONTENT_TYPE_OPTIONS = Object.freeze([
  Object.freeze({
    id: CONTENT_TYPE_BLANK,
    label: "Blank",
    status: "ready",
  }),
  Object.freeze({
    id: CONTENT_TYPE_RTF,
    label: "RTF Text",
    status: "ready",
  }),
  Object.freeze({
    id: CONTENT_TYPE_TABLE,
    label: "Table",
    status: "ready",
  }),
  Object.freeze({
    id: "smartphrase",
    label: "SmartPhrase",
    status: "coming-soon",
    soonLabel: "SmartPhrase content is not available yet.",
  }),
  Object.freeze({
    id: "emr-data",
    label: "EMR Data",
    status: "coming-soon",
    soonLabel: "EMR data binding is not available yet.",
  }),
  Object.freeze({
    id: "image",
    label: "Image",
    status: "coming-soon",
    soonLabel: "image content is not available yet.",
  }),
]);

/** Behavior options. Only None is functional in this phase. */
export const BEHAVIOR_OPTIONS = Object.freeze([
  Object.freeze({
    id: BEHAVIOR_NONE,
    label: "None",
    status: "ready",
  }),
  Object.freeze({
    id: "button",
    label: "Button / Clickable",
    status: "coming-soon",
    soonLabel: "click actions are not wired yet. A small card can later behave as a button.",
  }),
  Object.freeze({
    id: "open-page",
    label: "Open Page",
    status: "coming-soon",
    soonLabel: "page navigation is not available yet.",
  }),
  Object.freeze({
    id: "open-section",
    label: "Open Section",
    status: "coming-soon",
    soonLabel: "section navigation is not available yet.",
  }),
  Object.freeze({
    id: "run-smartphrase",
    label: "Run SmartPhrase",
    status: "coming-soon",
    soonLabel: "running a SmartPhrase from a card is not available yet.",
  }),
]);

function optionById(options, id) {
  return options.find((option) => option.id === id) || null;
}

export function contentTypeOption(id) {
  return optionById(CONTENT_TYPE_OPTIONS, id) || CONTENT_TYPE_OPTIONS[0];
}

export function behaviorOption(id) {
  return optionById(BEHAVIOR_OPTIONS, id) || BEHAVIOR_OPTIONS[0];
}

/** Grid V1 opens blank. Cards are added later; do not seed demo cards. */
export const DEFAULT_CARDS = Object.freeze([]);

/** Subtle teal used only for cards created with + Add New Card. */
export const NEW_CARD_BACKGROUND = "#E8F4F2";
export const NEW_CARD_BORDER = "#5E9E90";
export const NEW_CARD_PREFERRED_WIDTH = 2;
export const NEW_CARD_PREFERRED_HEIGHT = 2;
export const NO_GRID_SPACE_MESSAGE =
  `No available space on the ${GRID_SIZE_LABEL}. Resize or move an existing card.`;
const CARD_ID_RE = /^card-\d+$/;
const CARD_TITLE_RE = /^CARD\s+(\d+)$/i;

function asInt(value, fallback) {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

export function cloneCards(cards = DEFAULT_CARDS) {
  return (cards || []).map((card) => ({ ...card }));
}

export function defaultCards() {
  return cloneCards(DEFAULT_CARDS);
}

export function axisLabels(count) {
  const length = Math.max(0, asInt(count, 0));
  return Array.from({ length }, (_, index) => index + 1);
}

export function gridColumnLabels(columns = GRID_COLUMNS) {
  return axisLabels(columns);
}

export function gridRowLabels(rows = GRID_ROWS) {
  return axisLabels(rows);
}

export function gridAxisLabels() {
  return gridColumnLabels();
}

export function gridCellCount(columns = GRID_COLUMNS, rows = GRID_ROWS) {
  return columns * rows;
}

/**
 * Square-cell metrics for any column/row combination.
 * Cell size is derived from available width so cells never become rectangles.
 */
export function gridMetrics(
  availableWidth,
  { columns = GRID_COLUMNS, rows = GRID_ROWS, baseCellSize = CELL_SIZE } = {},
) {
  const width = Math.max(0, Number(availableWidth) || 0);
  const cols = Math.max(1, asInt(columns, GRID_COLUMNS));
  const rowCount = Math.max(1, asInt(rows, GRID_ROWS));
  const cellSize = width > 0 ? width / cols : baseCellSize;
  return Object.freeze({
    columns: cols,
    rows: rowCount,
    cellSize,
    gridWidth: cellSize * cols,
    gridHeight: cellSize * rowCount,
  });
}

export function gridCssVars({
  columns = GRID_COLUMNS,
  rows = GRID_ROWS,
  cellSize = CELL_SIZE,
} = {}) {
  return {
    "--epd-cols": String(columns),
    "--epd-rows": String(rows),
    "--epd-cell-base": `${cellSize}px`,
  };
}

export function cardsOverlap(a, b) {
  if (!a || !b || a.id === b.id) return false;
  return (
    a.col < b.col + b.width &&
    a.col + a.width > b.col &&
    a.row < b.row + b.height &&
    a.row + a.height > b.row
  );
}

export function layoutHasOverlap(cards) {
  for (let i = 0; i < cards.length; i += 1) {
    for (let j = i + 1; j < cards.length; j += 1) {
      if (cardsOverlap(cards[i], cards[j])) return true;
    }
  }
  return false;
}

export function cardIsInsideGrid(card) {
  return (
    card.col >= 1 &&
    card.row >= 1 &&
    card.width >= MIN_CARD_SIZE &&
    card.height >= MIN_CARD_SIZE &&
    card.col + card.width - 1 <= GRID_COLUMNS &&
    card.row + card.height - 1 <= GRID_ROWS
  );
}

export function layoutIsInsideGrid(cards) {
  return (cards || []).every(cardIsInsideGrid);
}

export function clampMove(card) {
  const width = Math.max(MIN_CARD_SIZE, asInt(card.width, MIN_CARD_SIZE));
  const height = Math.max(MIN_CARD_SIZE, asInt(card.height, MIN_CARD_SIZE));
  const maxCol = GRID_COLUMNS - width + 1;
  const maxRow = GRID_ROWS - height + 1;
  return {
    ...card,
    width,
    height,
    col: Math.min(Math.max(1, asInt(card.col, 1)), maxCol),
    row: Math.min(Math.max(1, asInt(card.row, 1)), maxRow),
  };
}

export function clampResize(card) {
  const col = Math.min(Math.max(1, asInt(card.col, 1)), GRID_COLUMNS);
  const row = Math.min(Math.max(1, asInt(card.row, 1)), GRID_ROWS);
  const maxWidth = GRID_COLUMNS - col + 1;
  const maxHeight = GRID_ROWS - row + 1;
  return {
    ...card,
    col,
    row,
    width: Math.min(Math.max(MIN_CARD_SIZE, asInt(card.width, MIN_CARD_SIZE)), maxWidth),
    height: Math.min(Math.max(MIN_CARD_SIZE, asInt(card.height, MIN_CARD_SIZE)), maxHeight),
  };
}

export function clampCard(card) {
  return clampResize(clampMove(card));
}

export function cellDeltaFromPixels(dx, dy, cellWidth, cellHeight) {
  const width = Number(cellWidth) || 0;
  const height = Number(cellHeight) || 0;
  return {
    col: width ? Math.round(Number(dx) / width) : 0,
    row: height ? Math.round(Number(dy) / height) : 0,
  };
}

export function canvasCellSize(rect, columns = GRID_COLUMNS) {
  const width = Number(rect?.width) || 0;
  const cols = Math.max(1, asInt(columns, GRID_COLUMNS));
  const cellSize = width > 0 ? width / cols : 0;
  return {
    cellWidth: cellSize,
    cellHeight: cellSize,
  };
}

export function findCard(cards, id) {
  return (cards || []).find((card) => card.id === id) || null;
}

/**
 * Apply a patch if the result stays on the grid and does not overlap.
 * Returns a new cards array, or null when the change is invalid.
 */
export function tryUpdateCard(cards, id, patch, mode = "move") {
  const current = findCard(cards, id);
  if (!current) return null;
  const merged = { ...current, ...patch };
  const nextCard = mode === "resize" ? clampResize(merged) : clampMove(merged);
  if (!cardIsInsideGrid(nextCard)) return null;
  const colliding = (cards || []).some((card) => card.id !== id && cardsOverlap(nextCard, card));
  if (colliding) return null;
  return (cards || []).map((card) => (card.id === id ? nextCard : card));
}

function walkCardChange(cards, id, targetPatch, mode) {
  const current = findCard(cards, id);
  if (!current) return null;
  const merged = { ...current, ...targetPatch };
  const target = mode === "resize" ? clampResize(merged) : clampMove(merged);
  const dCol = (mode === "resize" ? target.width : target.col) - (mode === "resize" ? current.width : current.col);
  const dRow = (mode === "resize" ? target.height : target.row) - (mode === "resize" ? current.height : current.row);
  const steps = Math.max(Math.abs(dCol), Math.abs(dRow));
  if (steps === 0) return cards;
  let best = null;
  for (let step = 1; step <= steps; step += 1) {
    const nextPatch =
      mode === "resize"
        ? {
            width: current.width + Math.round((dCol * step) / steps),
            height: current.height + Math.round((dRow * step) / steps),
          }
        : {
            col: current.col + Math.round((dCol * step) / steps),
            row: current.row + Math.round((dRow * step) / steps),
          };
    const next = tryUpdateCard(cards, id, nextPatch, mode);
    if (!next) break;
    best = next;
  }
  return best;
}

/**
 * Grid-snapped move: sets the card's cell position and clears any fine
 * pixel offset, so a snapped move always lands exactly on the grid.
 */
export function tryMoveCard(cards, id, col, row) {
  const next = walkCardChange(cards, id, { col, row }, "move");
  if (!next) return null;
  return next.map((card) => (card.id === id ? { ...card, offsetX: 0, offsetY: 0 } : card));
}

/**
 * Grid-snapped resize: sets the card's cell size and clears any fine
 * pixel size offset, so a snapped resize always lands exactly on the
 * grid — the resize counterpart of tryMoveCard.
 */
export function tryResizeCard(cards, id, width, height) {
  const next = walkCardChange(cards, id, { width, height }, "resize");
  if (!next) return null;
  return next.map((card) => (card.id === id ? { ...card, widthOffset: 0, heightOffset: 0 } : card));
}

/**
 * Free pixel offset from the card's grid cell. Used when Snap to Grid is
 * off (drag) and for keyboard arrow-key nudges, which stay sub-grid
 * regardless of the snap setting. Does not touch col/row, so grid
 * occupancy/overlap rules are unaffected.
 */
export function setCardFreeOffset(cards, id, offsetX, offsetY) {
  const current = findCard(cards, id);
  if (!current) return null;
  const nextOffsetX = Number.isFinite(Number(offsetX)) ? Number(offsetX) : 0;
  const nextOffsetY = Number.isFinite(Number(offsetY)) ? Number(offsetY) : 0;
  return (cards || []).map((card) =>
    card.id === id ? { ...card, offsetX: nextOffsetX, offsetY: nextOffsetY } : card,
  );
}

/** Nudge a card's pixel offset by a relative amount (keyboard arrow keys). */
export function nudgeCardPixels(cards, id, dx, dy) {
  const current = findCard(cards, id);
  if (!current) return null;
  const offsetX = (Number(current.offsetX) || 0) + (Number(dx) || 0);
  const offsetY = (Number(current.offsetY) || 0) + (Number(dy) || 0);
  return setCardFreeOffset(cards, id, offsetX, offsetY);
}

/**
 * Moves a group of selected cards to explicit absolute cell targets at
 * once, atomically: either every card in `moves` lands on its target (and
 * has its pixel offset cleared, like tryMoveCard) or, if any target would
 * leave the grid or the resulting layout would overlap, none of them move.
 * `moves` is an array of `{ id, col, row }`. This is the multi-card
 * counterpart of tryMoveCard, used for dragging or nudging 2+ selected
 * cards together while keeping their relative positions; a lone card in
 * `moves` still goes through the same bounds/overlap check tryMoveCard
 * itself uses, just without its per-step "slide to the last open cell"
 * walk (each card's target here is a fixed absolute delta from the drag
 * anchor, not a value to walk toward).
 */
export function tryMoveCardsTo(cards, moves) {
  const list = Array.isArray(moves) ? moves : [];
  if (list.length === 0) return null;
  const patches = new Map();
  for (const { id, col, row } of list) {
    const current = findCard(cards, id);
    if (!current) return null;
    const nextCard = { ...current, col: asInt(col, current.col), row: asInt(row, current.row) };
    if (!cardIsInsideGrid(nextCard)) return null;
    patches.set(id, nextCard);
  }
  const candidate = (cards || []).map((card) =>
    patches.has(card.id) ? { ...patches.get(card.id), offsetX: 0, offsetY: 0 } : card,
  );
  if (layoutHasOverlap(candidate)) return null;
  return candidate;
}

/**
 * Sets the free pixel offset for a group of selected cards at once, e.g.
 * a multi-card free (Snap to Grid off) drag. `moves` is an array of
 * `{ id, offsetX, offsetY }`. Like setCardFreeOffset, this never checks
 * bounds/collision — free offsets are sub-grid and always allowed.
 */
export function setCardsFreeOffset(cards, moves) {
  const list = Array.isArray(moves) ? moves : [];
  return list.reduce(
    (current, { id, offsetX, offsetY }) => setCardFreeOffset(current, id, offsetX, offsetY) || current,
    cards,
  );
}

/**
 * Nudges every selected card's pixel offset by the same relative amount —
 * the multi-card counterpart of nudgeCardPixels, used for keyboard
 * arrow-key movement when 2+ cards are selected. A single id behaves
 * identically to calling nudgeCardPixels directly.
 */
export function nudgeCardsPixels(cards, ids, dx, dy) {
  const selected = selectedCards(cards, ids, 1);
  if (!selected) return null;
  return selected.reduce((current, card) => nudgeCardPixels(current, card.id, dx, dy) || current, cards);
}

/**
 * Sets a card's rendered size to an exact pixel value, e.g. from the
 * Position & Size panel's Width (px) / Height (px) fields.
 *
 * With `snap: true` (default) the pixel value is rounded to the nearest
 * whole grid cell and applied through the same tryResizeCard path as the
 * grid Width/Height fields and the resize handle, so it follows the same
 * bounds/overlap rules and can be rejected (returns null) exactly like
 * those do.
 *
 * With `snap: false` the card's cell width/height stay fixed and only
 * widthOffset/heightOffset change — the resize counterpart of
 * setCardFreeOffset. The result is clamped (never rejected) so the card
 * never shrinks below MIN_PIXEL_CARD_SIZE or extends past the canvas
 * edge, accounting for the card's own position offset.
 */
export function trySetCardPixelSize(cards, id, widthPx, heightPx, { snap = true, cellSize = CELL_SIZE } = {}) {
  const current = findCard(cards, id);
  if (!current) return null;
  const size = Number(cellSize) > 0 ? Number(cellSize) : CELL_SIZE;

  if (snap) {
    const width = Math.round((Number(widthPx) || 0) / size);
    const height = Math.round((Number(heightPx) || 0) / size);
    return tryResizeCard(cards, id, width, height);
  }

  const leftPx = (current.col - 1) * size + (Number(current.offsetX) || 0);
  const topPx = (current.row - 1) * size + (Number(current.offsetY) || 0);
  const maxWidthPx = Math.max(MIN_PIXEL_CARD_SIZE, GRID_COLUMNS * size - leftPx);
  const maxHeightPx = Math.max(MIN_PIXEL_CARD_SIZE, GRID_ROWS * size - topPx);
  const nextWidthPx = Math.min(Math.max(Number(widthPx) || 0, MIN_PIXEL_CARD_SIZE), maxWidthPx);
  const nextHeightPx = Math.min(Math.max(Number(heightPx) || 0, MIN_PIXEL_CARD_SIZE), maxHeightPx);

  return (cards || []).map((card) =>
    card.id === id
      ? {
          ...card,
          widthOffset: Math.round(nextWidthPx - current.width * size),
          heightOffset: Math.round(nextHeightPx - current.height * size),
        }
      : card,
  );
}

const ALIGN_LEFT = "left";
const ALIGN_RIGHT = "right";
const ALIGN_TOP = "top";
const ALIGN_BOTTOM = "bottom";
const ALIGN_CENTER_HORIZONTAL = "center-horizontal";
const ALIGN_CENTER_VERTICAL = "center-vertical";

/** Resolves ids to cards for a multi-card op, or null when the group doesn't meet `minCount`. */
function selectedCards(cards, ids, minCount = 1) {
  const uniqueIds = Array.from(new Set(ids || []));
  if (uniqueIds.length < minCount) return null;
  const selected = uniqueIds.map((id) => findCard(cards, id));
  if (selected.some((card) => !card)) return null;
  return selected;
}

/** Selected cards for an alignment op, or null when the group is not alignable. */
function alignableSelection(cards, ids) {
  return selectedCards(cards, ids, 2);
}

/**
 * Grid-cell alignment (Snap to Grid on). Target edges/centers are computed
 * once from the selection's own bounding box in cell units, then every
 * selected card is moved to a whole cell — the same "snapped move" contract
 * as tryMoveCard, including clearing offsetX/offsetY. Validated against the
 * whole resulting layout (bounds + overlap) and rejected atomically: either
 * every selected card moves, or none do.
 */
function alignCardsSnapped(cards, ids, edge) {
  const selected = alignableSelection(cards, ids);
  if (!selected) return null;

  const left = Math.min(...selected.map((card) => card.col));
  const right = Math.max(...selected.map((card) => card.col + card.width));
  const top = Math.min(...selected.map((card) => card.row));
  const bottom = Math.max(...selected.map((card) => card.row + card.height));

  const patches = new Map();
  for (const card of selected) {
    let col = card.col;
    let row = card.row;
    switch (edge) {
      case ALIGN_LEFT:
        col = left;
        break;
      case ALIGN_RIGHT:
        col = right - card.width;
        break;
      case ALIGN_TOP:
        row = top;
        break;
      case ALIGN_BOTTOM:
        row = bottom - card.height;
        break;
      case ALIGN_CENTER_HORIZONTAL:
        col = Math.round((left + right) / 2 - card.width / 2);
        break;
      case ALIGN_CENTER_VERTICAL:
        row = Math.round((top + bottom) / 2 - card.height / 2);
        break;
      default:
        return null;
    }
    const nextCard = { ...card, col, row };
    if (!cardIsInsideGrid(nextCard)) return null;
    patches.set(card.id, nextCard);
  }

  const candidate = (cards || []).map((card) =>
    patches.has(card.id) ? { ...patches.get(card.id), offsetX: 0, offsetY: 0 } : card,
  );
  if (layoutHasOverlap(candidate)) return null;
  return candidate;
}

/**
 * Pixel-precise alignment (Snap to Grid off). Uses each card's actual
 * rendered geometry — grid cell anchor plus its fine offsetX/offsetY — via
 * the caller-supplied cell size, mirroring the math cardStyle() uses to
 * render cards. Only offsetX/offsetY change; col/row are left untouched,
 * the same convention setCardFreeOffset/nudgeCardPixels already use for
 * free (sub-grid) positioning. Rejected atomically when a card would land
 * outside the canvas.
 */
function alignCardsFree(cards, ids, edge, cellWidth, cellHeight) {
  const selected = alignableSelection(cards, ids);
  if (!selected) return null;
  const width = Number(cellWidth) > 0 ? Number(cellWidth) : CELL_SIZE;
  const height = Number(cellHeight) > 0 ? Number(cellHeight) : CELL_SIZE;

  const boxes = new Map(
    selected.map((card) => {
      const offsetX = Number(card.offsetX) || 0;
      const offsetY = Number(card.offsetY) || 0;
      const cardLeft = (card.col - 1) * width + offsetX;
      const cardTop = (card.row - 1) * height + offsetY;
      return [
        card.id,
        {
          left: cardLeft,
          top: cardTop,
          right: cardLeft + card.width * width,
          bottom: cardTop + card.height * height,
        },
      ];
    }),
  );

  const left = Math.min(...selected.map((card) => boxes.get(card.id).left));
  const right = Math.max(...selected.map((card) => boxes.get(card.id).right));
  const top = Math.min(...selected.map((card) => boxes.get(card.id).top));
  const bottom = Math.max(...selected.map((card) => boxes.get(card.id).bottom));
  const canvasWidth = GRID_COLUMNS * width;
  const canvasHeight = GRID_ROWS * height;
  const EPSILON = 0.01;

  const patches = new Map();
  for (const card of selected) {
    const box = boxes.get(card.id);
    const cardWidthPx = card.width * width;
    const cardHeightPx = card.height * height;
    let nextLeft = box.left;
    let nextTop = box.top;
    switch (edge) {
      case ALIGN_LEFT:
        nextLeft = left;
        break;
      case ALIGN_RIGHT:
        nextLeft = right - cardWidthPx;
        break;
      case ALIGN_TOP:
        nextTop = top;
        break;
      case ALIGN_BOTTOM:
        nextTop = bottom - cardHeightPx;
        break;
      case ALIGN_CENTER_HORIZONTAL:
        nextLeft = (left + right) / 2 - cardWidthPx / 2;
        break;
      case ALIGN_CENTER_VERTICAL:
        nextTop = (top + bottom) / 2 - cardHeightPx / 2;
        break;
      default:
        return null;
    }
    const nextRight = nextLeft + cardWidthPx;
    const nextBottom = nextTop + cardHeightPx;
    if (
      nextLeft < -EPSILON ||
      nextTop < -EPSILON ||
      nextRight > canvasWidth + EPSILON ||
      nextBottom > canvasHeight + EPSILON
    ) {
      return null;
    }
    patches.set(card.id, {
      offsetX: Math.round(nextLeft - (card.col - 1) * width),
      offsetY: Math.round(nextTop - (card.row - 1) * height),
    });
  }

  return (cards || []).map((card) =>
    patches.has(card.id) ? { ...card, ...patches.get(card.id) } : card,
  );
}

/**
 * Aligns the selected cards atomically (all move, or none do). With
 * `snap: true` (default) it aligns on whole grid cells and follows the
 * existing bounds/overlap rules, like a snapped move. With `snap: false`
 * it aligns on the actual rendered pixel geometry — including offsetX/
 * offsetY — and only ever adjusts that pixel offset, like a free drag.
 * `cellWidth`/`cellHeight` should be the live canvas cell size (see
 * canvasCellSize); they are only used when `snap` is false.
 */
function alignCards(cards, ids, edge, { snap = true, cellWidth = CELL_SIZE, cellHeight = CELL_SIZE } = {}) {
  return snap
    ? alignCardsSnapped(cards, ids, edge)
    : alignCardsFree(cards, ids, edge, cellWidth, cellHeight);
}

export function alignCardsLeft(cards, ids, options) {
  return alignCards(cards, ids, ALIGN_LEFT, options);
}

export function alignCardsRight(cards, ids, options) {
  return alignCards(cards, ids, ALIGN_RIGHT, options);
}

export function alignCardsTop(cards, ids, options) {
  return alignCards(cards, ids, ALIGN_TOP, options);
}

export function alignCardsBottom(cards, ids, options) {
  return alignCards(cards, ids, ALIGN_BOTTOM, options);
}

/** Aligns selected cards so their horizontal (X) centers match. */
export function alignCardsCenterHorizontal(cards, ids, options) {
  return alignCards(cards, ids, ALIGN_CENTER_HORIZONTAL, options);
}

/** Aligns selected cards so their vertical (Y) centers match. */
export function alignCardsCenterVertical(cards, ids, options) {
  return alignCards(cards, ids, ALIGN_CENTER_VERTICAL, options);
}

const DISTRIBUTE_HORIZONTAL = "horizontal";
const DISTRIBUTE_VERTICAL = "vertical";

/**
 * Grid-cell distribute (Snap to Grid on). Requires 3+ selected cards — with
 * only 2 there is nothing "between" them to space out. Sorts the selection
 * along the axis, holds the two extreme cards (by leading edge) fixed, and
 * spaces every card in between so the gaps between adjacent edges are equal,
 * matching the leftover space (bounding span minus the sum of every card's
 * own size) evenly across the gaps. Validated against the whole resulting
 * layout (bounds + overlap) and rejected atomically, like alignCardsSnapped.
 */
function distributeCardsSnapped(cards, ids, axis) {
  const selected = selectedCards(cards, ids, 3);
  if (!selected) return null;
  const posKey = axis === DISTRIBUTE_HORIZONTAL ? "col" : "row";
  const sizeKey = axis === DISTRIBUTE_HORIZONTAL ? "width" : "height";

  const sorted = [...selected].sort((a, b) => a[posKey] - b[posKey]);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const span = last[posKey] + last[sizeKey] - first[posKey];
  const sumSizes = sorted.reduce((total, card) => total + card[sizeKey], 0);
  const gap = (span - sumSizes) / (sorted.length - 1);

  const patches = new Map();
  let cursor = first[posKey] + first[sizeKey];
  for (let i = 1; i < sorted.length - 1; i += 1) {
    const card = sorted[i];
    cursor += gap;
    const pos = Math.round(cursor);
    const nextCard = { ...card, [posKey]: pos };
    if (!cardIsInsideGrid(nextCard)) return null;
    patches.set(card.id, nextCard);
    cursor = pos + card[sizeKey];
  }

  const candidate = (cards || []).map((card) =>
    patches.has(card.id) ? { ...patches.get(card.id), offsetX: 0, offsetY: 0 } : card,
  );
  if (layoutHasOverlap(candidate)) return null;
  return candidate;
}

/**
 * Pixel-precise distribute (Snap to Grid off). Same "equalize the gaps
 * between the two extreme cards" logic as distributeCardsSnapped, but
 * measured on each card's actual rendered geometry (grid cell anchor plus
 * its fine offsetX/offsetY), mirroring alignCardsFree. Only offsetX/offsetY
 * change; col/row are left untouched. Rejected atomically when a card would
 * land outside the canvas.
 */
function distributeCardsFree(cards, ids, axis, cellWidth, cellHeight) {
  const selected = selectedCards(cards, ids, 3);
  if (!selected) return null;
  const horizontal = axis === DISTRIBUTE_HORIZONTAL;
  const width = Number(cellWidth) > 0 ? Number(cellWidth) : CELL_SIZE;
  const height = Number(cellHeight) > 0 ? Number(cellHeight) : CELL_SIZE;
  const cellSize = horizontal ? width : height;

  const boxes = new Map(
    selected.map((card) => {
      const offsetX = Number(card.offsetX) || 0;
      const offsetY = Number(card.offsetY) || 0;
      const start = horizontal ? (card.col - 1) * width + offsetX : (card.row - 1) * height + offsetY;
      const size = (horizontal ? card.width : card.height) * cellSize;
      return [card.id, { start, size }];
    }),
  );

  const sorted = [...selected].sort((a, b) => boxes.get(a.id).start - boxes.get(b.id).start);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const firstBox = boxes.get(first.id);
  const lastBox = boxes.get(last.id);
  const span = lastBox.start + lastBox.size - firstBox.start;
  const sumSizes = sorted.reduce((total, card) => total + boxes.get(card.id).size, 0);
  const gap = (span - sumSizes) / (sorted.length - 1);
  const canvasLimit = horizontal ? GRID_COLUMNS * width : GRID_ROWS * height;
  const EPSILON = 0.01;

  const patches = new Map();
  let cursor = firstBox.start + firstBox.size;
  for (let i = 1; i < sorted.length - 1; i += 1) {
    const card = sorted[i];
    const box = boxes.get(card.id);
    cursor += gap;
    const nextStart = cursor;
    const nextEnd = nextStart + box.size;
    if (nextStart < -EPSILON || nextEnd > canvasLimit + EPSILON) return null;
    patches.set(
      card.id,
      horizontal
        ? { offsetX: Math.round(nextStart - (card.col - 1) * width) }
        : { offsetY: Math.round(nextStart - (card.row - 1) * height) },
    );
    cursor = nextStart + box.size;
  }

  return (cards || []).map((card) => (patches.has(card.id) ? { ...card, ...patches.get(card.id) } : card));
}

/**
 * Distributes the selected cards' spacing evenly along one axis, atomically
 * (all move, or none do). Same `snap`/`cellWidth`/`cellHeight` contract as
 * alignCards. Requires 3+ selected cards.
 */
function distributeCards(cards, ids, axis, { snap = true, cellWidth = CELL_SIZE, cellHeight = CELL_SIZE } = {}) {
  return snap ? distributeCardsSnapped(cards, ids, axis) : distributeCardsFree(cards, ids, axis, cellWidth, cellHeight);
}

/** Distributes selected cards so the horizontal gaps between them are equal. */
export function distributeCardsHorizontally(cards, ids, options) {
  return distributeCards(cards, ids, DISTRIBUTE_HORIZONTAL, options);
}

/** Distributes selected cards so the vertical gaps between them are equal. */
export function distributeCardsVertically(cards, ids, options) {
  return distributeCards(cards, ids, DISTRIBUTE_VERTICAL, options);
}

/** Selected cards for a Same Width/Height/Size op — the same 2+ threshold as alignment. */
function sameSizeSelection(cards, ids) {
  return selectedCards(cards, ids, 2);
}

/**
 * Resizes every selected card to the given width/height (grid cells),
 * anchored at its own existing col/row — the same top-left anchor
 * tryResizeCard uses — atomically: either every card fits on the grid
 * without overlapping another card, or none of them resize. Clears
 * widthOffset/heightOffset like tryResizeCard, since a grid-driven resize
 * always supersedes any leftover pixel size offset.
 */
function matchCardsDimensions(cards, ids, dims) {
  const selected = sameSizeSelection(cards, ids);
  if (!selected) return null;
  const patches = new Map();
  for (const card of selected) {
    const width = dims.width ?? card.width;
    const height = dims.height ?? card.height;
    const nextCard = { ...card, width, height };
    if (!cardIsInsideGrid(nextCard)) return null;
    patches.set(card.id, nextCard);
  }
  const candidate = (cards || []).map((card) =>
    patches.has(card.id) ? { ...patches.get(card.id), widthOffset: 0, heightOffset: 0 } : card,
  );
  if (layoutHasOverlap(candidate)) return null;
  return candidate;
}

/** Sets every selected card's width to match the reference card's width. */
export function matchCardsWidth(cards, ids, referenceId) {
  const reference = findCard(cards, referenceId);
  if (!reference) return null;
  return matchCardsDimensions(cards, ids, { width: reference.width });
}

/** Sets every selected card's height to match the reference card's height. */
export function matchCardsHeight(cards, ids, referenceId) {
  const reference = findCard(cards, referenceId);
  if (!reference) return null;
  return matchCardsDimensions(cards, ids, { height: reference.height });
}

/** Sets every selected card's width and height to match the reference card's size. */
export function matchCardsSize(cards, ids, referenceId) {
  const reference = findCard(cards, referenceId);
  if (!reference) return null;
  return matchCardsDimensions(cards, ids, { width: reference.width, height: reference.height });
}

export function tryStyleCard(cards, id, patch) {
  const current = findCard(cards, id);
  if (!current) return null;
  return (cards || []).map((card) => (card.id === id ? { ...card, ...patch } : card));
}

export function removeCard(cards, id) {
  if (!id || !findCard(cards, id)) return null;
  return (cards || []).filter((card) => card.id !== id);
}

export function duplicateCard(cards, id) {
  const source = findCard(cards, id);
  if (!source) return null;
  const placement = findFirstAvailablePlacement(
    cards,
    source.width,
    source.height,
  );
  if (!placement) return null;
  const number = nextCardNumber(cards);
  return [
    ...(cards || []),
    {
      ...source,
      id: `card-${number}`,
      title: `CARD ${number}`,
      col: placement.col,
      row: placement.row,
      width: placement.width,
      height: placement.height,
      offsetX: 0,
      offsetY: 0,
      widthOffset: 0,
      heightOffset: 0,
    },
  ];
}

function defaultById(id) {
  return DEFAULT_CARDS.find((card) => card.id === id) || null;
}

function occupancyGrid(cards) {
  const occupied = Array.from({ length: GRID_ROWS }, () => Array(GRID_COLUMNS).fill(false));
  (cards || []).forEach((card) => {
    if (!cardIsInsideGrid(card)) return;
    for (let row = card.row; row < card.row + card.height; row += 1) {
      for (let col = card.col; col < card.col + card.width; col += 1) {
        occupied[row - 1][col - 1] = true;
      }
    }
  });
  return occupied;
}

export function nextCardNumber(cards) {
  let max = 0;
  (cards || []).forEach((card) => {
    const titleMatch = typeof card?.title === "string" ? card.title.trim().match(CARD_TITLE_RE) : null;
    const idMatch = typeof card?.id === "string" ? card.id.trim().match(/^card-(\d+)$/i) : null;
    if (titleMatch) max = Math.max(max, Number.parseInt(titleMatch[1], 10));
    if (idMatch) max = Math.max(max, Number.parseInt(idMatch[1], 10));
  });
  return max + 1;
}

/**
 * First empty cell in row-major order, sized to the preferred 2×2
 * placeholder or whatever remaining rectangle fits from that origin.
 */
export function findFirstAvailablePlacement(
  cards,
  preferredWidth = NEW_CARD_PREFERRED_WIDTH,
  preferredHeight = NEW_CARD_PREFERRED_HEIGHT,
) {
  const occupied = occupancyGrid(cards);
  const wantWidth = Math.max(MIN_CARD_SIZE, asInt(preferredWidth, NEW_CARD_PREFERRED_WIDTH));
  const wantHeight = Math.max(MIN_CARD_SIZE, asInt(preferredHeight, NEW_CARD_PREFERRED_HEIGHT));
  for (let row = 1; row <= GRID_ROWS; row += 1) {
    for (let col = 1; col <= GRID_COLUMNS; col += 1) {
      if (occupied[row - 1][col - 1]) continue;
      let maxWidth = 0;
      for (let nextCol = col; nextCol <= GRID_COLUMNS; nextCol += 1) {
        if (occupied[row - 1][nextCol - 1]) break;
        maxWidth += 1;
      }
      let maxHeight = 0;
      heightLoop: for (let nextRow = row; nextRow <= GRID_ROWS; nextRow += 1) {
        for (let nextCol = col; nextCol < col + maxWidth; nextCol += 1) {
          if (occupied[nextRow - 1][nextCol - 1]) break heightLoop;
        }
        maxHeight += 1;
      }
      if (maxWidth < MIN_CARD_SIZE || maxHeight < MIN_CARD_SIZE) continue;
      return {
        col,
        row,
        width: Math.min(wantWidth, maxWidth),
        height: Math.min(wantHeight, maxHeight),
      };
    }
  }
  return null;
}

export function createPlaceholderCard(cards) {
  const placement = findFirstAvailablePlacement(cards);
  if (!placement) return null;
  const number = nextCardNumber(cards);
  return {
    id: `card-${number}`,
    title: `CARD ${number}`,
    ...placement,
    background: NEW_CARD_BACKGROUND,
    border: NEW_CARD_BORDER,
    contentType: CONTENT_TYPE_BLANK,
    behavior: BEHAVIOR_NONE,
    tableContent: emptyTableContent(),
    offsetX: 0,
    offsetY: 0,
    widthOffset: 0,
    heightOffset: 0,
  };
}

export function addPlaceholderCard(cards) {
  const card = createPlaceholderCard(cards);
  if (!card) return null;
  return [...(cards || []), card];
}

function sanitizeColor(value, fallback) {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) return trimmed;
  return fallback;
}

function sanitizeTitle(value, fallback) {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed || fallback;
}

function sanitizeChoice(value, options, fallback) {
  const id = typeof value === "string" ? value.trim() : "";
  return options.some((option) => option.id === id) ? id : fallback;
}

export function normalizeLayout(rawCards) {
  if (!Array.isArray(rawCards) || rawCards.length === 0) {
    return defaultCards();
  }
  const seen = new Set();
  const next = [];
  for (const saved of rawCards) {
    const id = typeof saved?.id === "string" ? saved.id.trim() : "";
    if (!CARD_ID_RE.test(id) || seen.has(id)) return defaultCards();
    seen.add(id);
    const defaults = defaultById(id);
    const number = Number.parseInt(id.slice(5), 10);
    next.push(
      clampCard({
        id,
        title: sanitizeTitle(saved?.title, defaults?.title || `CARD ${number}`),
        col: asInt(saved?.col, defaults?.col ?? 1),
        row: asInt(saved?.row, defaults?.row ?? 1),
        width: asInt(saved?.width, defaults?.width ?? NEW_CARD_PREFERRED_WIDTH),
        height: asInt(saved?.height, defaults?.height ?? NEW_CARD_PREFERRED_HEIGHT),
        background: sanitizeColor(
          saved?.background,
          defaults?.background || NEW_CARD_BACKGROUND,
        ),
        border: sanitizeColor(saved?.border, defaults?.border || NEW_CARD_BORDER),
        contentType: sanitizeChoice(
          saved?.contentType,
          CONTENT_TYPE_OPTIONS,
          defaults?.contentType || CONTENT_TYPE_BLANK,
        ),
        behavior: sanitizeChoice(
          saved?.behavior,
          BEHAVIOR_OPTIONS,
          defaults?.behavior || BEHAVIOR_NONE,
        ),
        rtfHtml: sanitizeRtfHtml(saved?.rtfHtml || defaults?.rtfHtml || ""),
        tableContent: sanitizeTableContent(saved?.tableContent || defaults?.tableContent),
        offsetX: asInt(saved?.offsetX, defaults?.offsetX ?? 0),
        offsetY: asInt(saved?.offsetY, defaults?.offsetY ?? 0),
        widthOffset: asInt(saved?.widthOffset, defaults?.widthOffset ?? 0),
        heightOffset: asInt(saved?.heightOffset, defaults?.heightOffset ?? 0),
      }),
    );
  }
  if (!layoutIsInsideGrid(next) || layoutHasOverlap(next)) return defaultCards();
  return next;
}

export function layoutEquals(left, right) {
  if (!left || !right || left.length !== right.length) return false;
  return left.every((card, index) => {
    const other = right[index];
    return (
      card.id === other.id &&
      card.title === other.title &&
      card.col === other.col &&
      card.row === other.row &&
      card.width === other.width &&
      card.height === other.height &&
      card.background === other.background &&
      card.border === other.border &&
      (card.contentType || CONTENT_TYPE_BLANK) === (other.contentType || CONTENT_TYPE_BLANK) &&
      (card.behavior || BEHAVIOR_NONE) === (other.behavior || BEHAVIOR_NONE) &&
      (card.rtfHtml || "") === (other.rtfHtml || "") &&
      (card.offsetX || 0) === (other.offsetX || 0) &&
      (card.offsetY || 0) === (other.offsetY || 0) &&
      (card.widthOffset || 0) === (other.widthOffset || 0) &&
      (card.heightOffset || 0) === (other.heightOffset || 0) &&
      tableContentEquals(card.tableContent, other.tableContent)
    );
  });
}

export function applyTableContent(cards, id, tableContent) {
  const current = findCard(cards, id);
  if (!current) return null;
  let next = tryStyleCard(cards, id, {
    contentType: CONTENT_TYPE_TABLE,
    tableContent: sanitizeTableContent(tableContent),
  });
  if (!next) return null;
  const card = findCard(next, id);
  const stillPlaceholder =
    card &&
    card.width <= NEW_CARD_PREFERRED_WIDTH &&
    card.height <= NEW_CARD_PREFERRED_HEIGHT;
  if (stillPlaceholder) {
    const grown = tryResizeCard(next, id, TABLE_PREFERRED_WIDTH, TABLE_PREFERRED_HEIGHT);
    if (grown) next = grown;
  }
  return next;
}
